import { useAtomValue, useSetAtom } from 'jotai'
import { useNavigate, useSearchParams } from 'react-router-dom'
import * as VerifyEmailStore from '../stores/VerifyEmailStore'
import { forwardRef } from 'react'
import { pageLinks } from '../../pageLinks'
import { OTPInput, SlotProps } from 'input-otp'

// ============================================================================
// INLINE UI COMPONENTS - self-contained, no external dependencies
// ============================================================================

function Card({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={className} {...props}>
      {children}
    </div>
  )
}

function CardHeader({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={className} {...props}>
      {children}
    </div>
  )
}

function CardTitle({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 className={className} {...props}>
      {children}
    </h3>
  )
}

function CardDescription({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={`text-sm text-neutral-500 ${className}`} {...props}>
      {children}
    </p>
  )
}

function CardContent({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={className} {...props}>
      {children}
    </div>
  )
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'outline'
}

function Button({
  className = '',
  variant = 'default',
  children,
  ...props
}: ButtonProps) {
  const variantStyles =
    variant === 'default'
      ? 'bg-neutral-900 text-white'
      : 'bg-neutral-100 text-neutral-900'

  return (
    <button
      className={`inline-flex items-center justify-center text-sm h-10 px-4 py-2 disabled:opacity-50 disabled:pointer-events-none ${variantStyles} ${className}`}
      {...props}
    >
      {children}
    </button>
  )
}

const Input = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ className = '', ...props }, ref) => {
  return (
    <input
      ref={ref}
      className={`flex h-10 w-full border-b border-neutral-200 bg-transparent px-0 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 disabled:opacity-50 ${className}`}
      {...props}
    />
  )
})

function Label({
  className = '',
  children,
  ...props
}: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label
      className={`text-sm text-neutral-500 ${className}`}
      {...props}
    >
      {children}
    </label>
  )
}

interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'destructive'
}

function Alert({
  className = '',
  variant = 'default',
  children,
  ...props
}: AlertProps) {
  const variantStyles =
    variant === 'destructive'
      ? 'text-red-600'
      : 'text-neutral-900'

  return (
    <div
      role="alert"
      className={`text-sm ${variantStyles} ${className}`}
      {...props}
    >
      {children}
    </div>
  )
}

function AlertDescription({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={`text-sm ${className}`} {...props}>
      {children}
    </p>
  )
}

// ============================================================================
// FIELD COMPONENTS - directly connected to Jotai store
// ============================================================================

function OtpSlot({ char, hasFakeCaret, isActive }: SlotProps) {
  return (
    <div
      className={`relative w-11 h-14 sm:w-14 sm:h-16 flex items-center justify-center text-2xl border-b-2 ${
        isActive
          ? 'border-neutral-900'
          : char !== null
            ? 'border-neutral-400'
            : 'border-neutral-200'
      }`}
    >
      <span className={char !== null ? 'text-neutral-900' : 'text-neutral-300'}>
        {char}
      </span>
      {hasFakeCaret && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-px h-6 bg-neutral-900 animate-pulse" />
        </div>
      )}
    </div>
  )
}

function OtpField() {
  const { otp } = useAtomValue(VerifyEmailStore.verifyEmailFormAtom)
  const errors = useAtomValue(VerifyEmailStore.verifyEmailFormErrorsAtom)
  const updateForm = useSetAtom(VerifyEmailStore.updateVerifyEmailFormAtom)

  return (
    <div className="space-y-4">
      <Label htmlFor="otp" className="sr-only">
        Verification code
      </Label>
      <div className="max-w-sm mx-auto">
        <OTPInput
          id="otp"
          value={otp}
          onChange={(value) => updateForm({ otp: value })}
          maxLength={6}
          autoFocus
          containerClassName="flex justify-between gap-3"
          aria-invalid={!!errors?.otp}
          render={({ slots }) => (
            <>
              {slots.map((slot, index) => (
                <OtpSlot key={index} {...slot} />
              ))}
            </>
          )}
        />
      </div>

      {errors?.otp ? (
        <p className="text-center text-sm text-red-600">{errors.otp}</p>
      ) : (
        <p className="text-center text-sm text-neutral-400">
          Enter the security code we just sent you
        </p>
      )}
    </div>
  )
}

// ============================================================================
// LAYOUT COMPONENT - defined outside to prevent re-creation on every render
// ============================================================================

function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-sm">{children}</div>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function VerifyEmail() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email')

  const { otp, isSubmitting, isResending } = useAtomValue(
    VerifyEmailStore.verifyEmailFormAtom,
  )
  const errors = useAtomValue(VerifyEmailStore.verifyEmailFormErrorsAtom)
  const isSuccess = useAtomValue(VerifyEmailStore.verifyEmailSuccessAtom)
  const resendSuccess = useAtomValue(
    VerifyEmailStore.verifyEmailResendSuccessAtom,
  )
  const submitVerifyEmail = useSetAtom(VerifyEmailStore.submitVerifyEmailAtom)
  const resendVerificationOtp = useSetAtom(
    VerifyEmailStore.resendVerificationOtpAtom,
  )

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    if (email) {
      await submitVerifyEmail(email)
    }
  }

  const handleResend = () => {
    if (email) {
      resendVerificationOtp(email)
    }
  }

  if (!email) {
    return (
      <Layout>
        <Card>
          <CardHeader className="text-center pb-6 space-y-1">
            <CardTitle className="text-xl text-neutral-900">
              Access Denied
            </CardTitle>
            <CardDescription>
              The verification link appears to be invalid or incomplete.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate(pageLinks.global_shortcut_login())}
              variant="outline"
              className="w-full"
            >
              Back to Sign In
            </Button>
          </CardContent>
        </Card>
      </Layout>
    )
  }

  if (isSuccess) {
    return (
      <Layout>
        <Card>
          <CardHeader className="text-center pb-6 space-y-1">
            <CardTitle className="text-xl text-neutral-900">
              All set!
            </CardTitle>
            <CardDescription>
              Your email has been successfully verified. Welcome to the
              platform.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate(pageLinks.global_shortcut_home())}
              className="w-full"
            >
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </Layout>
    )
  }

  return (
    <Layout>
      <Card>
        <CardHeader className="text-center pb-6 space-y-1">
          <CardTitle className="text-xl text-neutral-900">
            Verify your email
          </CardTitle>
          <CardDescription>
            We've sent a 6-digit verification code to{' '}
            <span className="text-neutral-900 block mt-1">{email}</span>
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form className="space-y-8" onSubmit={handleVerify}>
            {errors?.form && (
              <Alert variant="destructive">
                <AlertDescription className="text-center">
                  {errors.form}
                </AlertDescription>
              </Alert>
            )}

            {resendSuccess && (
              <Alert>
                <AlertDescription className="text-center text-neutral-500">
                  Code resent successfully
                </AlertDescription>
              </Alert>
            )}

            <OtpField />

            <div className="space-y-4">
              <Button
                type="submit"
                disabled={isSubmitting || otp.length !== 6}
                className="w-full"
              >
                {isSubmitting ? 'Verifying...' : 'Confirm Verification'}
              </Button>

              <div className="flex flex-col items-center gap-4 pt-2">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={isResending}
                  className="text-sm text-neutral-400 hover:text-neutral-900 disabled:opacity-50"
                >
                  {isResending ? 'Sending new code...' : 'Resend code'}
                </button>

                <div className="w-full h-px bg-neutral-200" />

                <button
                  type="button"
                  onClick={() => navigate(pageLinks.global_shortcut_login())}
                  className="text-sm text-neutral-400 hover:text-neutral-900"
                >
                  Back to login
                </button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </Layout>
  )
}
